import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DayTemplate } from '@/types';
import { X, RotateCcw, Plus } from 'lucide-react';

interface DayTemplateApplicatorProps {
  template: DayTemplate;
  targetDate: Date;
  onApply: (replace: boolean) => void;
  onCancel: () => void;
  hasExistingShifts: boolean;
}

const DayTemplateApplicator: React.FC<DayTemplateApplicatorProps> = ({
  template,
  targetDate,
  onApply,
  onCancel,
  hasExistingShifts,
}) => {
  // Handle ESC key to cancel template application
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div className="p-2 space-y-2">
      <div className="flex justify-between items-center">
        <h4 className="font-medium text-sm">Apply Template</h4>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">
          Apply <strong>{template.name}</strong> to <strong>{targetDate.toLocaleDateString()}</strong>?
        </p>
      </div>
      <div className="flex flex-col space-y-2">
        <Button
          variant="default"
          size="xs"
          onClick={() => onApply(true)}
          className="w-full justify-start h-auto py-1 whitespace-normal text-left"
        >
          <RotateCcw className="h-3 w-3 mr-1 flex-shrink-0" />
          Replace All Shifts
        </Button>
        <Button
          variant="outline"
          size="xs"
          onClick={() => onApply(false)}
          className="w-full justify-start h-auto py-1 whitespace-normal text-left"
        >
          <Plus className="h-3 w-3 mr-1 flex-shrink-0" />
          Add to Current Shifts
        </Button>
        <Button
          variant="ghost"
          size="xs"
          onClick={onCancel}
          className="w-full justify-start h-auto py-1 whitespace-normal text-left"
        >
          <X className="h-3 w-3 mr-1 flex-shrink-0" />
          Cancel
        </Button>
      </div>
      {hasExistingShifts && (
        <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
          <strong>Note:</strong> "Replace All Shifts" will delete all existing shifts for this day.
        </div>
      )}
    </div>
  );
};

export default DayTemplateApplicator;
